import api from '@/api/client';

const state = {
  vacationChecklist: null,
  vacationChecklistByKey: {},
  vacationChecklistCategories: [],
  vacationChecklistAssignableUsers: [],
  vacationChecklistLoading: false,
  vacationChecklistSaving: false,
  vacationChecklistError: null,
};

const getters = {
  stateVacationChecklist: state => state.vacationChecklist,
  stateVacationChecklistCategories: state => state.vacationChecklistCategories,
  stateVacationChecklists: state => state.vacationChecklistByKey,
  stateVacationAssignableUsers: state => state.vacationChecklistAssignableUsers,
  stateVacationChecklistLoading: state => state.vacationChecklistLoading,
  stateVacationChecklistSaving: state => state.vacationChecklistSaving,
  stateVacationChecklistError: state => state.vacationChecklistError,
  getVacationChecklistByKey: state => key => state.vacationChecklistByKey[key] || null,
};

const actions = {
  async loadVacationChecklists({ commit }) {
    commit('setVacationChecklistLoading', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.get('/vacation-checklists');
      commit('setVacationChecklists', Array.isArray(data) ? data : []);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistLoading', false);
    }
  },

  async loadVacationChecklist({ commit }, categoryKey) {
    commit('setVacationChecklistLoading', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.get(`/vacation-checklists/${encodeURIComponent(categoryKey)}`);
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistLoading', false);
    }
  },

  async loadVacationChecklistCategories({ commit }) {
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.get('/vacation-checklists/categories');
      commit('setVacationChecklistCategories', Array.isArray(data) ? data : []);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    }
  },

  async loadVacationChecklistAssignableUsers({ commit }) {
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.get('/vacation-checklists/assignable-users');
      commit('setVacationChecklistAssignableUsers', Array.isArray(data) ? data : []);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    }
  },

  async loadVacationChecklistParticipants({ commit }, categoryKey) {
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.get(`/vacation-checklists/${encodeURIComponent(categoryKey)}/participants`);
      return normalizeUserIds(data?.participant_user_ids);
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    }
  },

  async setVacationChecklistParticipants({ commit }, { categoryKey, participantUserIds }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.put(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/participants`,
        { participant_user_ids: normalizeUserIds(participantUserIds) }
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async createVacationChecklistCategory({ dispatch, commit }, payload) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.post('/vacation-checklists/categories', normalizeCategoryPayload(payload));
      commit('setVacationChecklist', data);
      await dispatch('loadVacationChecklistCategories');
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async updateVacationChecklistCategory({ dispatch, commit }, { categoryKey, patch }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.patch(
        `/vacation-checklists/categories/${encodeURIComponent(categoryKey)}`,
        normalizeCategoryPatch(patch)
      );
      commit('setVacationChecklist', data);
      await dispatch('loadVacationChecklistCategories');
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async deleteVacationChecklistCategory({ dispatch, commit }, categoryKey) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.delete(`/vacation-checklists/categories/${encodeURIComponent(categoryKey)}`);
      commit('removeVacationChecklistCategory', categoryKey);
      await dispatch('loadVacationChecklistCategories');
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async createVacationChecklistSection({ commit }, { categoryKey, sectionKey, title, sortOrder }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const payload = {
        title: String(title || '').trim(),
      };
      const normalizedSectionKey = normalizeKey(sectionKey);
      if (normalizedSectionKey) payload.section_key = normalizedSectionKey;
      if (sortOrder !== undefined && sortOrder !== null && sortOrder !== '') payload.sort_order = Number(sortOrder);

      const { data } = await api.post(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/sections`,
        payload
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async updateVacationChecklistSection({ commit }, { categoryKey, sectionKey, patch }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.patch(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/sections/${encodeURIComponent(sectionKey)}`,
        normalizeSectionPatch(patch)
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async deleteVacationChecklistSection({ dispatch, commit }, { categoryKey, sectionKey }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.delete(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/sections/${encodeURIComponent(sectionKey)}`
      );
      await dispatch('loadVacationChecklist', categoryKey);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async setVacationChecklistItemChecked({ commit }, { categoryKey, itemId, checked }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.put(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/items/${encodeURIComponent(itemId)}/checked`,
        { checked: Boolean(checked) }
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async setVacationChecklistItemAssignedUsers({ commit }, { categoryKey, itemId, assignedUserIds }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.put(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/items/${encodeURIComponent(itemId)}/assigned-users`,
        { assigned_user_ids: normalizeUserIds(assignedUserIds) }
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async createVacationChecklistItem({ commit }, { categoryKey, sectionKey, text, sortOrder, assignedUserIds, sourceItemId }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const payload = {
        text: String(text || '').trim(),
      };
      if (hasValue(sourceItemId)) {
        payload.source_item_id = Number(sourceItemId);
        delete payload.text;
      }
      if (hasValue(sortOrder)) payload.sort_order = Number(sortOrder);
      if (assignedUserIds) payload.assigned_user_ids = normalizeUserIds(assignedUserIds);

      const { data } = await api.post(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/sections/${encodeURIComponent(sectionKey)}/items`,
        payload
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async updateVacationChecklistItem({ commit }, { categoryKey, itemId, patch }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.patch(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/items/${encodeURIComponent(itemId)}`,
        normalizeItemPatch(patch)
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async moveVacationChecklistItemToSection({ commit }, { categoryKey, itemId, sectionKey }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.patch(
        `/vacation-checklists/${encodeURIComponent(categoryKey)}/items/${encodeURIComponent(itemId)}/section`,
        { section_key: String(sectionKey || '').trim() }
      );
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async deleteVacationChecklistItem({ dispatch, commit }, { categoryKey, itemId }) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      await api.delete(`/vacation-checklists/${encodeURIComponent(categoryKey)}/items/${encodeURIComponent(itemId)}`);
      return await dispatch('loadVacationChecklist', categoryKey);
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  async resetVacationChecklist({ commit }, categoryKey) {
    commit('setVacationChecklistSaving', true);
    commit('setVacationChecklistError', null);
    try {
      const { data } = await api.post(`/vacation-checklists/${encodeURIComponent(categoryKey)}/reset`);
      commit('setVacationChecklist', data);
      return data;
    } catch (error) {
      commit('setVacationChecklistError', error);
      throw error;
    } finally {
      commit('setVacationChecklistSaving', false);
    }
  },

  updateVacationChecklistItemChecked({ dispatch }, { categoryKey, itemId, checked }) {
    return dispatch('setVacationChecklistItemChecked', { categoryKey, itemId, checked });
  },

  updateVacationChecklistItemUsers({ dispatch }, { categoryKey, itemId, assignedUserIds }) {
    return dispatch('setVacationChecklistItemAssignedUsers', { categoryKey, itemId, assignedUserIds });
  },

  moveVacationChecklistItem({ dispatch }, { categoryKey, itemId, sectionKey }) {
    return dispatch('moveVacationChecklistItemToSection', {
      categoryKey,
      itemId,
      sectionKey,
    });
  },

  addVacationChecklistItem({ dispatch }, { categoryKey, sectionId, sectionKey, text, sourceItemId }) {
    return dispatch('createVacationChecklistItem', {
      categoryKey,
      sectionKey: sectionKey || sectionId,
      text,
      sourceItemId,
    });
  },
};

const mutations = {
  setVacationChecklist(state, checklist) {
    const normalized = normalizeChecklist(checklist);
    state.vacationChecklist = normalized;
    if (normalized?.key) {
      state.vacationChecklistByKey = {
        ...state.vacationChecklistByKey,
        [normalized.key]: normalized,
      };
    }
  },

  setVacationChecklists(state, checklists) {
    const byKey = {};
    checklists.map(normalizeChecklist).forEach((checklist) => {
      if (checklist?.key) byKey[checklist.key] = checklist;
    });
    state.vacationChecklistByKey = byKey;
  },

  setVacationChecklistCategories(state, categories) {
    state.vacationChecklistCategories = categories
      .map(category => ({
        id: category.id,
        key: category.key,
        title: category.title,
        sort_order: category.sort_order ?? category.sortOrder ?? 0,
        enabled: category.enabled !== false,
      }))
      .filter(category => category.enabled)
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  },

  removeVacationChecklistCategory(state, categoryKey) {
    const byKey = { ...state.vacationChecklistByKey };
    delete byKey[categoryKey];
    state.vacationChecklistByKey = byKey;
    state.vacationChecklistCategories = state.vacationChecklistCategories.filter(category => category.key !== categoryKey);
    if (state.vacationChecklist?.key === categoryKey) {
      state.vacationChecklist = null;
    }
  },

  setVacationChecklistAssignableUsers(state, users) {
    state.vacationChecklistAssignableUsers = users
      .map(user => ({
        id: Number(user.id),
        name: String(user.name || '').trim(),
      }))
      .filter(user => Number.isFinite(user.id) && user.id > 0 && user.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  },

  setVacationChecklistLoading(state, loading) {
    state.vacationChecklistLoading = Boolean(loading);
  },

  setVacationChecklistSaving(state, saving) {
    state.vacationChecklistSaving = Boolean(saving);
  },

  setVacationChecklistError(state, error) {
    state.vacationChecklistError = error || null;
  },
};

function normalizeChecklist(checklist) {
  if (!checklist || typeof checklist !== 'object') return null;

  return {
    key: checklist.key,
    title: checklist.title,
    participant_user_ids: normalizeUserIds(checklist.participant_user_ids ?? checklist.participantUserIds),
    sections: (Array.isArray(checklist.sections) ? checklist.sections : [])
      .map(normalizeSection)
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)),
  };
}

function normalizeSection(section) {
  return {
    id: section.id,
    title: section.title,
    sort_order: section.sort_order ?? section.sortOrder ?? 0,
    items: (Array.isArray(section.items) ? section.items : [])
      .map(normalizeItem)
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)),
  };
}

function normalizeItem(item) {
  return {
    id: item.id,
    text: item.text,
    checked: Boolean(item.checked),
    custom: Boolean(item.custom),
    reusable_item_key: item.reusable_item_key ?? item.reusableItemKey ?? '',
    sort_order: item.sort_order ?? item.sortOrder ?? 0,
    assigned_user_ids: normalizeUserIds(item.assigned_user_ids ?? item.assignedUserIds),
  };
}

function normalizeUserIds(ids) {
  return Array.from(new Set(Array.isArray(ids) ? ids : []))
    .map(id => Number(id))
    .filter(id => Number.isFinite(id) && id > 0)
    .slice(0, 5);
}

function normalizeItemPatch(patch = {}) {
  const payload = {};
  if (patch.text !== undefined) payload.text = String(patch.text || '').trim();
  if (hasValue(patch.sort_order)) payload.sort_order = Number(patch.sort_order);
  if (hasValue(patch.sortOrder)) payload.sort_order = Number(patch.sortOrder);
  if (hasValue(patch.section_key)) payload.section_key = String(patch.section_key).trim();
  if (hasValue(patch.sectionKey)) payload.section_key = String(patch.sectionKey).trim();
  if (patch.checked !== undefined) payload.checked = Boolean(patch.checked);
  if (patch.assigned_user_ids !== undefined) payload.assigned_user_ids = normalizeUserIds(patch.assigned_user_ids);
  if (patch.assignedUserIds !== undefined) payload.assigned_user_ids = normalizeUserIds(patch.assignedUserIds);
  return payload;
}

function normalizeKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
}

function normalizeCategoryPayload(payload = {}) {
  const categoryKey = normalizeKey(payload.category_key ?? payload.categoryKey ?? payload.key);
  const result = {
    title: String(payload.title || '').trim(),
  };
  if (categoryKey) result.category_key = categoryKey;
  if (payload.sort_order !== undefined && payload.sort_order !== null && payload.sort_order !== '') result.sort_order = Number(payload.sort_order);
  if (payload.sortOrder !== undefined && payload.sortOrder !== null && payload.sortOrder !== '') result.sort_order = Number(payload.sortOrder);
  if (payload.enabled !== undefined) result.enabled = Boolean(payload.enabled);
  const sourceCategoryKey = normalizeKey(payload.source_category_key ?? payload.sourceCategoryKey);
  if (sourceCategoryKey) result.source_category_key = sourceCategoryKey;
  if (Array.isArray(payload.sections)) {
    result.sections = payload.sections
      .map(section => {
        const normalizedSection = {
          title: String(section.title || '').trim(),
          sort_order: section.sort_order ?? section.sortOrder,
        };
        const sectionKey = normalizeKey(section.section_key ?? section.sectionKey ?? section.id);
        if (sectionKey) normalizedSection.section_key = sectionKey;
        return normalizedSection;
      })
      .filter(section => section.title)
      .map(section => {
        if (section.sort_order === undefined || section.sort_order === null || section.sort_order === '') {
          delete section.sort_order;
        } else {
          section.sort_order = Number(section.sort_order);
        }
        return section;
      });
  }
  return result;
}

function normalizeCategoryPatch(patch = {}) {
  const payload = {};
  if (patch.title !== undefined) payload.title = String(patch.title || '').trim();
  if (hasValue(patch.sort_order)) payload.sort_order = Number(patch.sort_order);
  if (hasValue(patch.sortOrder)) payload.sort_order = Number(patch.sortOrder);
  if (patch.enabled !== undefined) payload.enabled = Boolean(patch.enabled);
  return payload;
}

function normalizeSectionPatch(patch = {}) {
  const payload = {};
  if (patch.title !== undefined) payload.title = String(patch.title || '').trim();
  if (hasValue(patch.sort_order)) payload.sort_order = Number(patch.sort_order);
  if (hasValue(patch.sortOrder)) payload.sort_order = Number(patch.sortOrder);
  return payload;
}

function hasValue(value) {
  return value !== undefined && value !== null && value !== '';
}

export default {
  state,
  getters,
  actions,
  mutations,
};
